import { Box, Alert } from '@mui/material'
import { FunctionComponent, useEffect, useState, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import FileUploadService from '../../../../../../shared/services/file-upload/file-upload.service'
import { IFileUpload } from '../../../../../../shared/services/file-upload/file-upload.model'
import { LoadingSpinner } from '../../../../../presentation-components/display-elements-components/loading-spinner'
import { ImportStepHeader } from './import-step-header'
import { ImportProcessingIndicator } from '../../../../../presentation-components/display-elements-components/import-processing-indicator'

export const FileUploadProgress: FunctionComponent<{
  /** Embedded in the wizard: poll this upload rather than the route param. */
  fileUploadId?: string
  /** Embedded in the wizard: the wizard renders its own step header above this content. */
  hideStepHeader?: boolean
  /**
   * Embedded in the wizard: called once processing has finished, after a short success
   * beat, instead of the standalone page's redirect. Also called when a bill upload fails
   * as a whole, because the per-document reasons live on the review step.
   */
  onContinue?: (fileUpload: IFileUpload) => void
}> = ({ fileUploadId: fileUploadIdProp, hideStepHeader = false, onContinue }) => {
  const { fileUploadId: fileUploadIdFromRoute } = useParams<{ fileUploadId: string }>()
  const fileUploadId = fileUploadIdProp || fileUploadIdFromRoute
  const navigate = useNavigate()
  const fileUploadServiceRef = useRef(new FileUploadService())
  const [fileUpload, setFileUpload] = useState<IFileUpload | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCompleted, setIsCompleted] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // The wizard's callback, held in a ref so the polling and completion effects do not
  // restart every time the parent re-renders with a fresh arrow function.
  const onContinueRef = useRef(onContinue)
  onContinueRef.current = onContinue

  const fetchProgress = useCallback(async () => {
    if (!fileUploadId) {
      setError('File upload ID is missing')
      setIsLoading(false)
      return
    }
    try {
      const data = await fileUploadServiceRef.current.checkFileUploadProgress(fileUploadId)
      setFileUpload(data)
      setIsLoading(false)
      setError(null)
      // Check if import has failed
      if (data.lineImportStatus === 'FAILED') {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
        // A bill import fails as a whole only when every document was unreadable. Send the user to
        // the review screen rather than import history: that is where the per-document reason lives
        // ("this PDF is password-protected"), along with the manual-entry fallback. Embedded in the
        // wizard that means advancing to the review step in place.
        if (data.sourceType === 'BILL_DOCUMENTS') {
          if (onContinueRef.current) {
            onContinueRef.current(data)
          } else {
            navigate(`/settings/data-upload/import-data?fileUploadId=${fileUploadId}&step=2`)
          }
          return
        }
        // A spreadsheet that fails at this stage has no rows to inspect, so the history list would
        // show the user a red chip and nothing else. Show the recorded reason here instead.
        const reason = data.failureReason
        if (reason) {
          setError(reason)
          return
        }
        // No recorded reason. Embedded, stay put with an honest line rather than yanking the whole
        // wizard off to the history list; standalone keeps its original redirect.
        if (onContinueRef.current) {
          setError('The file could not be processed. You can check the upload in Import History.')
          return
        }
        navigate('/settings/data-upload/import-history')
        return
      }
      // Check if import is completed
      const totalRecords = data.recordCount ?? 0
      const currentRecords = data.numberOfRecords ?? 0
      // Mark as completed if the backend says so, or the counts line up:
      // 1. the backend has flagged the import COMPLETED (authoritative), OR
      // 2. we have a recordCount and currentRecords >= recordCount, OR
      // 3. recordCount is not set/0 but we have records (assume complete)
      const shouldComplete = data.lineImportStatus === 'COMPLETED' ||
        (totalRecords > 0 && currentRecords >= totalRecords) ||
        (totalRecords === 0 && currentRecords > 0)
      if (shouldComplete) {
        setIsCompleted(true)
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
      }
    } catch (err: any) {
      console.error('Error fetching progress:', err)
      setError(err?.response?.data?.message || 'Failed to fetch upload progress')
      setIsLoading(false)
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [fileUploadId, navigate])

  useEffect(() => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    // Initial fetch
    fetchProgress()
    // Set up polling every 5 seconds only if not completed
    if (!isCompleted) {
      pollingIntervalRef.current = setInterval(() => {
        fetchProgress()
      }, 5000)
    }
    // Cleanup on unmount or when completed
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [fileUploadId, fetchProgress, isCompleted])

  // On completion: hold the success state briefly, then hand over. Embedded in the wizard
  // that is a callback and the step advances in place; standalone it is the original redirect.
  useEffect(() => {
    if (isCompleted && fileUploadId && !isProcessing) {
      redirectTimeoutRef.current = setTimeout(() => {
        setIsProcessing(true)
        if (onContinueRef.current) {
          if (fileUpload) {
            onContinueRef.current(fileUpload)
          }
          return
        }
        // Navigate after showing processing message
        setTimeout(() => {
          navigate(`/settings/data-upload/import-data?fileUploadId=${fileUploadId}&step=2`)
        }, 100)
      }, onContinueRef.current ? 1500 : 2000)
    }

    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current)
        redirectTimeoutRef.current = null
      }
    }
  }, [isCompleted, fileUploadId, isProcessing, navigate, fileUpload])

  // Calculate progress percentage
  const getProgressPercentage = () => {
    // A bill import has no lines at all until every document has been read, so the line-count
    // measure would sit at 0% for the whole of extraction and then jump to 100%. Report the
    // document-level progress the backend supplies instead: that is what is actually happening.
    if (fileUpload?.extractionProgress) {
      return fileUpload.extractionProgress.percentComplete
    }
    if (!fileUpload || !fileUpload.recordCount || fileUpload.recordCount === 0) {
      return 0
    }
    const percentage = (fileUpload.numberOfRecords / fileUpload.recordCount) * 100
    return Math.min(percentage, 100)
  }

  const progressPercentage = getProgressPercentage()
  const totalSteps = fileUpload?.linkToExistingEmission ? 5 : 4

  if (isLoading && !fileUpload) {
    return (
      <Box sx={{ py: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <LoadingSpinner />
        </Box>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ py: 1 }}>
        {!hideStepHeader && <ImportStepHeader currentStep={2} totalSteps={totalSteps} />}
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    )
  }

  if (!fileUpload) {
    return (
      <Box sx={{ py: 1 }}>
        {!hideStepHeader && <ImportStepHeader currentStep={2} totalSteps={totalSteps} />}
        <Alert severity="warning">
          File upload not found
        </Alert>
      </Box>
    )
  }

  const extraction = fileUpload.extractionProgress
  const isBillImport = fileUpload.sourceType === 'BILL_DOCUMENTS'

  return (
    <Box sx={{ py: 1 }}>
      {!hideStepHeader && <ImportStepHeader currentStep={2} totalSteps={totalSteps} />}
      <Box sx={{ mt: 2 }}>
        {isBillImport ? (
          <ImportProcessingIndicator
            percent={progressPercentage}
            // Counted in documents, not rows: the row count isn't known until every bill is read,
            // and one bill can yield several rows (a dual-fuel bill, a fuel-card statement).
            rowsDone={extraction?.extractedDocuments}
            rowsTotal={extraction?.totalDocuments}
            unitLabel='documents'
            complete={isCompleted}
            indeterminate={!isCompleted && !extraction?.totalDocuments}
            scale={1.1}
            maxWidth={880}
            title="Reading your bills"
            subtitle="Finding the consumption, dates and meter details on each bill"
            completeTitle="Bills read"
            // Without this the shared default reads "1 documents imported successfully", wrong on
            // both counts. Nothing is imported at this point: the bills have only been read, and the
            // import happens after the user confirms what was found.
            completeSubtitle={
              extraction?.totalDocuments === 1
                ? 'Check what we found before importing'
                : `${extraction?.totalDocuments ?? 0} bills read. Check what we found before importing`
            }
            steps={['Upload', 'Read', 'Extract', 'Finalise']}
            messages={[
              'Uploading your bills…',
              'Reading each document…',
              'Pulling out the activity data…',
              'Almost there…',
            ]}
            footerText="Reading a bill takes longer than a spreadsheet, usually under a minute each. You can navigate away and check back later; it keeps running."
          />
        ) : (
          <ImportProcessingIndicator
            percent={progressPercentage}
            rowsDone={fileUpload.recordCount ? fileUpload.numberOfRecords : (fileUpload.numberOfRecords || undefined)}
            rowsTotal={fileUpload.recordCount || undefined}
            complete={isCompleted}
            indeterminate={!isCompleted && !fileUpload.recordCount}
            scale={1.1}
            maxWidth={880}
            title="Processing your file"
            subtitle="Reading and checking your spreadsheet"
            completeTitle="File processed"
            steps={['Upload', 'Validate', 'Parse', 'Finalise']}
            messages={[
              'Reading your spreadsheet…',
              'Validating rows…',
              'Checking for duplicate entries…',
              'Almost there…',
            ]}
            footerText="Large files can take a moment. You can navigate away and check back later; processing keeps running."
          />
        )}
      </Box>
    </Box>
  )
}

